import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';

import {
  answerChallenge,
  AuthError,
  beginSignIn,
  hasPreferredTotp,
  startSignIn,
  type SignInState,
} from '../auth/cognito.ts';
import { useSession } from '../auth/session.tsx';
import { Button } from '../components/button.tsx';
import { Wordmark } from '../components/brand.tsx';
import { Field } from '../components/field.tsx';
import { EnrolTotpScreen } from './enrol-totp.tsx';

// The slice 1 acceptance path (CB-CONV §12): the office signs in by password plus TOTP. Cognito
// decides the sequence, so this screen is driven by whatever step it returns rather than by an
// order assumed here — a first sign-in on a temporary password goes password → new password →
// TOTP setup, and every sign-in after that goes password → TOTP code.
//
// ADO-01: the screen says what to do next in plain words at every step, because the person reading
// it has no one to ask.
//
// Crew part 2 (CB-REQ ADO-02 at v1.964): the address comes first, and Cognito says what that
// account uses — a password for the office, an emailed code for crew. A code is typed into this
// same screen, so the session lands where it began (ADO-02: why the code is primary).
//
// **A password sign-in that ends without an authenticator goes to setup before the app.** Pool MFA is
// `OPTIONAL`, so Cognito never offers it, and the API only asks once a role crosses the price line —
// which provisioning refuses to grant until the factor exists (FND-01, FND-08). Without this step a
// new office account could never be given its role. Only the office has a password, so crew on the
// code path are never asked. This is the offer, not the enforcement: that stays in the API guard.

type Stage = SignInState['step'] | 'email' | 'enrol';

export function SignInScreen() {
  const session = useSession();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [totp, setTotp] = useState<{ setupUri: string; secret: string } | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // A ref, not state: the password step's own `run` reads it before React would re-render.
  const viaPassword = useRef(false);

  async function enter() {
    // Nothing here stores a token — Amplify holds it and refreshes it. The session provider re-reads
    // it so the shell knows someone is signed in, then the router leaves this screen.
    await session.refresh();
    await navigate({ to: '/' });
  }

  async function run(work: () => Promise<SignInState>) {
    setBusy(true);
    setError(undefined);
    try {
      const result = await work();
      if (result.step === 'done') {
        if (viaPassword.current && !(await hasPreferredTotp().catch(() => true))) {
          // A failed lookup lets them in rather than trapping them here: the API still refuses
          // anything above the price line without the factor.
          setStage('enrol');
          return;
        }
        await enter();
        return;
      }
      if (result.step === 'totp-setup') {
        setTotp({ setupUri: result.setupUri, secret: result.secret });
      }
      setStage(result.step);
    } catch (caught) {
      setError(
        caught instanceof AuthError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'enrol') return <EnrolTotpScreen onEnrolled={() => void enter()} />;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
      <div className="flex flex-col gap-4 border-b-2 border-divider pb-5">
        {/* The wordmark alone: the icon never sits beside it (brand pack v1.1). */}
        <Wordmark height={22} />
        <div>
          <h1 className="text-[28px] text-ink">Sign in</h1>
        </div>
      </div>

      {error ? (
        // role="alert" so the failure is announced, not only shown (INV-13).
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}

      {stage === 'email' ? (
        <EmailStep
          busy={busy}
          onSubmit={(address) => {
            viaPassword.current = false;
            setEmail(address);
            void run(() => beginSignIn(address));
          }}
        />
      ) : null}

      {stage === 'password' ? (
        <PasswordStep
          busy={busy}
          email={email}
          onBack={() => setStage('email')}
          onSubmit={(password) => {
            viaPassword.current = true;
            return run(() => startSignIn(email, password));
          }}
        />
      ) : null}

      {stage === 'email-code' ? (
        <EmailCodeStep
          busy={busy}
          email={email}
          onResend={() => run(() => beginSignIn(email))}
          onBack={() => setStage('email')}
          onSubmit={(code) => run(() => answerChallenge(code))}
        />
      ) : null}

      {stage === 'new-password-required' ? (
        <NewPasswordStep
          busy={busy}
          onSubmit={(password) => run(() => answerChallenge(password))}
        />
      ) : null}

      {stage === 'totp-setup' && totp ? (
        <TotpSetupStep
          busy={busy}
          setupUri={totp.setupUri}
          secret={totp.secret}
          onSubmit={(code) => run(() => answerChallenge(code))}
        />
      ) : null}

      {stage === 'totp-code' ? (
        <TotpCodeStep busy={busy} onSubmit={(code) => run(() => answerChallenge(code))} />
      ) : null}
    </main>
  );
}

function EmailStep({ busy, onSubmit }: { busy: boolean; onSubmit: (email: string) => void }) {
  const [email, setEmail] = useState('');
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit(email.trim());
      }}
    >
      <Field
        label="Email address"
        type="email"
        name="email"
        autoComplete="username"
        required
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Checking…' : 'Continue'}
      </Button>
    </form>
  );
}

function Signing({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <p className="text-sm text-ink-soft">
      Signing in as <span className="font-semibold text-ink">{email}</span> ·{' '}
      <button type="button" className="link" onClick={onBack}>
        Use a different email
      </button>
    </p>
  );
}

function PasswordStep({
  busy,
  email,
  onBack,
  onSubmit,
}: {
  busy: boolean;
  email: string;
  onBack: () => void;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState('');

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit(password);
      }}
    >
      <Signing email={email} onBack={onBack} />
      {/* The address again, hidden, so a password manager pairs the two (WCAG 2.2 3.3.8). */}
      <input type="hidden" name="email" autoComplete="username" value={email} />
      <Field
        label="Password"
        type="password"
        name="password"
        autoComplete="current-password"
        required
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}

function EmailCodeStep({
  busy,
  email,
  onResend,
  onBack,
  onSubmit,
}: {
  busy: boolean;
  email: string;
  onResend: () => void;
  onBack: () => void;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit(code.trim());
      }}
    >
      <Signing email={email} onBack={onBack} />
      <p>
        We’ve emailed you a code. Type it here — it works for a few minutes. Check your junk folder
        if it isn’t there.
      </p>
      <Field
        label="Code from the email"
        inputMode="numeric"
        autoComplete="one-time-code"
        required
        autoFocus
        value={code}
        onChange={(e) => setCode(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
      <button type="button" className="link self-start text-sm" onClick={onResend}>
        Send a new code
      </button>
    </form>
  );
}

function NewPasswordStep({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const mismatch = again.length > 0 && password !== again;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (!mismatch) onSubmit(password);
      }}
    >
      <p className="text-ink-soft">
        This account is using a temporary password. Choose a permanent one to continue.
      </p>
      <Field
        label="New password"
        type="password"
        name="new-password"
        autoComplete="new-password"
        required
        minLength={12}
        // The pool's policy is length only, no composition rules (ADR-0006 rule 2). Saying so beats
        // letting someone discover it by rejection.
        hint="At least 12 characters. No particular mix of letters, numbers or symbols is required."
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
      />
      <Field
        label="New password again"
        type="password"
        name="new-password-confirm"
        autoComplete="new-password"
        required
        error={mismatch ? 'The two passwords do not match.' : undefined}
        value={again}
        onChange={(e) => setAgain(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy || mismatch}>
        {busy ? 'Saving…' : 'Set password'}
      </Button>
    </form>
  );
}

function TotpSetupStep({
  busy,
  setupUri,
  secret,
  onSubmit,
}: {
  busy: boolean;
  setupUri: string;
  secret: string;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState('');

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit(code.trim());
      }}
    >
      <p className="text-ink-soft">
        Set up your authenticator app. Open it, add an account, and either use the link below or
        type the setup key by hand.
      </p>
      <a
        href={setupUri}
        className="text-accent-700 underline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        Open in your authenticator app
      </a>
      <div>
        <p className="text-sm font-medium text-ink">Setup key</p>
        {/* Selectable and broken into readable runs, because this gets typed on a phone. */}
        <code className="mt-1 block rounded bg-tile p-3 font-mono text-sm break-all text-ink">
          {secret}
        </code>
      </div>
      <Field
        label="Six-digit code from the app"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        required
        value={code}
        onChange={(e) => setCode(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Checking…' : 'Confirm and finish'}
      </Button>
    </form>
  );
}

function TotpCodeStep({ busy, onSubmit }: { busy: boolean; onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('');

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit(code.trim());
      }}
    >
      <Field
        label="Six-digit code from your authenticator app"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        required
        autoFocus
        value={code}
        onChange={(e) => setCode(e.currentTarget.value)}
      />
      <Button type="submit" disabled={busy}>
        {busy ? 'Checking…' : 'Continue'}
      </Button>
    </form>
  );
}
